import React from 'react';
import styled from 'styled-components';

declare module '@nucleus/react-components' {
  interface ReactComponentsTheme {
    Chip?: {
      background?: string;
      color?: string;
      borderRadius?: string;
      deleteColor?: string;
      deleteHoverColor?: string;
    };
  }
}

export type ChipSize = 'small' | 'medium';

export interface ChipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * The text content displayed inside the chip.
   */
  label: React.ReactNode;
  /**
   * Visual size of the chip.
   * @default 'medium'
   */
  size?: ChipSize;
  /**
   * Whether the chip should appear and behave as disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Optional icon shown to the left of the label.
   */
  icon?: React.ReactNode;
  /**
   * When provided, renders a delete button. Receives the click event when invoked.
   */
  onDelete?: (event: React.SyntheticEvent) => void;
  /**
   * When `onDelete` is omitted but the parent still wants a custom delete icon,
   * pass it here. Defaults to a simple "x" glyph.
   */
  deleteIcon?: React.ReactElement<{ onClick?: (event: React.MouseEvent) => void; className?: string }>;
  /**
   * Accessible label for the default delete button. When omitted, defaults to
   * `"Remove <label>"` when `label` is a string, otherwise `"Remove"`.
   */
  deleteAriaLabel?: string;
}

/**
 * The Chip component represents a compact element for showing a piece of information,
 * commonly used to render selected values inside an `Autocomplete` with `multiple` enabled.
 *
 * Inspired by [material-ui/chip](https://mui.com/material-ui/api/chip/).
 *
 * @param props - The props for the Chip component.
 * @returns A JSX element representing the Chip.
 */
export const Chip = ({
  label,
  size,
  disabled,
  icon,
  onDelete,
  deleteIcon,
  deleteAriaLabel,
  ...props
}: ChipProps): JSX.Element => {
  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete?.(event);
  };

  const renderDeleteIcon = () => {
    if (!onDelete) {
      return null;
    }
    if (deleteIcon) {
      return React.cloneElement(deleteIcon, {
        onClick: handleDeleteClick,
        className: ['ChipDeleteIcon', deleteIcon.props.className].filter(Boolean).join(' '),
      });
    }
    const resolvedDeleteAriaLabel =
      deleteAriaLabel ?? (typeof label === 'string' ? `Remove ${label}` : 'Remove');
    return (
      <DeleteIcon
        className="ChipDeleteIcon"
        type="button"
        onClick={handleDeleteClick}
        aria-label={resolvedDeleteAriaLabel}
      >
        ×
      </DeleteIcon>
    );
  };

  return (
    <Root {...props} data-size={size} data-disabled={disabled || undefined}>
      {icon && <IconSlot>{icon}</IconSlot>}
      <Label>{label}</Label>
      {renderDeleteIcon()}
    </Root>
  );
};

Chip.defaultProps = {
  size: 'medium' as ChipSize,
  disabled: false,
};

const Root = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  background: ${({ theme }) => theme._reactComponents.Chip?.background ?? 'rgba(0, 0, 0, 0.08)'};
  color: ${({ theme }) => theme._reactComponents.Chip?.color ?? 'inherit'};
  border-radius: ${({ theme }) => theme._reactComponents.Chip?.borderRadius ?? '16px'};
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  user-select: none;

  &[data-size='small'] {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
  }

  &[data-disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: -4px;
`;

const DeleteIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  margin: 0 -4px 0 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: ${({ theme }) => theme._reactComponents.Chip?.deleteColor ?? 'currentColor'};
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme._reactComponents.Chip?.deleteHoverColor ?? 'currentColor'};
  }
`;
